import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { useTranslation } from "react-i18next"
import { useTable, useFilters, useGlobalFilter, useSortBy, usePagination } from "react-table"
import { NavLink } from "react-router-dom"

import * as BicytrustApi from "features/bike/api/BicytrustApi"

import { isDefined } from "utils/validation"
import dateService from "services/date"
import { useAlert } from "hooks"
import { InsuranceFormSteps } from "features/bike/Partials/Bicytrust/InsuranceForm"

import ProtectedLayout from "layouts/Protected"
import { PenIcon } from "components/Elements/Icons"
import { HelpInfo } from "components/Elements/Help"
import { HelpCircleIcon } from "components/Elements/Icons"
import { CheckOutlineIcon } from "components/Elements/Icons/CheckOutline"

const PAGINATION_LENGTH = 10
export const BicytrustContractsPage: React.FC = () => {
	const { t } = useTranslation()
	const { alert } = useAlert()

	const [data, setData] = React.useState([])
	const [isLoading, setIsLoading] = React.useState(true)

	const fetchAllRequestState = React.useCallback(
		() => {
			;(async () => {
				try {
					const { res: req, getError } = await BicytrustApi.requestAllStates()
					if (getError()) throw getError()
					req.sort(function (a, b) {
						return (
							dateService.getTimestamp(b.modify_date.toString()) -
							dateService.getTimestamp(a.modify_date.toString())
						)
					})
					setData(req)
					setIsLoading(false)
				} catch (error) {
					//@ts-ignore
					const erroMsg = error?.message?.toString()
					if (erroMsg) alert("error", erroMsg)
					console.error(error)
				}
			})()
		},
		[] // eslint-disable-line
	)

	React.useEffect(() => fetchAllRequestState(), [])

	const customFilterFunction = (rows, columnId, filterValue) => {
		return rows.filter((row) => {
			const value = row.values[columnId]

			switch (filterValue) {
				case "canceled":
					return value === InsuranceFormSteps.CANCELED
				case "sent":
					return value === InsuranceFormSteps.SENT
				case "infos":
					return value === InsuranceFormSteps.INFOS
				case "signed":
					return value === InsuranceFormSteps.SIGNED
				default:
					return true
			}
		})
	}

	// Columns definition
	const columns = React.useMemo(
		() => {
			const baseCols = [
				{
					Header: t("insurance.list.fnuci", { ns: "bike" }),
					accessor: "bike_id",
				},
				{
					Header: t("insurance.list.price_bike", { ns: "bike" }),
					accessor: `bike_price`,
				},
				{
					Header: t("insurance.warranty_type", { ns: "bike" }),
					accessor: `pack_name`,
				},
				{
					Header: (
						<div className="flex">
							{t("insurance.list.price_year", { ns: "bike", year: "1" })}
						</div>
					),
					accessor: `prix_aa`,
				},
				{
					Header: t("insurance.list.date", { ns: "bike" }),
					accessor: `modify_date`,
					Cell: ({ value }) =>
						isDefined(value) && dateService.formatDate(value, "dd/MM/yyyy"),
				},
				{
					Header: t("insurance.seller", { ns: "bike" }),
					accessor: `seller_data.last_name`,
					Cell: ({ row, value }) => {
						if (row.original.seller_data)
							return `${row.original.seller_data.last_name} ${row.original.seller_data.first_name}`
						else return ""
					},
				},
				{
					Header: t("insurance.list.statut", { ns: "bike" }),
					accessor: (row) => {
						switch (row.status) {
							case InsuranceFormSteps.CANCELED:
								return t("insurance.list.statut_canceled", { ns: "bike" })
							case InsuranceFormSteps.SENT:
								return t("insurance.list.statut_sent", { ns: "bike" })
							case InsuranceFormSteps.SIGNED:
								return t("insurance.list.statut_signed", { ns: "bike" })
							default: // InsuranceFormSteps.INFOS:
								return t("insurance.list.statut_proposed", { ns: "bike" })
						}
					},
					filter: customFilterFunction,
					Cell: ({ row, value }) => {
						switch (row.original.status) {
							case InsuranceFormSteps.CANCELED:
								return t("insurance.list.statut_canceled", { ns: "bike" })
							case InsuranceFormSteps.SENT:
								return t("insurance.list.statut_sent", { ns: "bike" })
							case InsuranceFormSteps.SIGNED:
								return (
									<div className="flex">
										<CheckOutlineIcon size={20} color="#4caf50" />
										&nbsp;
										{t("insurance.list.statut_signed", { ns: "bike" })}
									</div>
								)
							default: // InsuranceFormSteps.INFOS
								return t("insurance.list.statut_proposed", { ns: "bike" })
						}
					},
				},
			]
			return baseCols
		},
		[t] // eslint-disable-line
	)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		// rows,
		page, // the rows for the active page

		// pagination utilities
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		setGlobalFilter,
		state: { pageIndex },
	} = useTable(
		{
			//@ts-ignore
			columns,
			data,
			initialState: {
				pageSize: 10,
			},
			// autoResetSelectedRows: false
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		usePagination
	)

	// Manage global search
	// -------------------
	const [dataTableFilter, setDataTableFilter] = React.useState("")
	React.useEffect(() => setGlobalFilter(dataTableFilter), [dataTableFilter, setGlobalFilter])

	// Manage pagination
	// -------------------
	const middleDistance = Math.round((PAGINATION_LENGTH - 1) / 2)
	const pageShouldBeHiddenInPagination = (index) => {
		// in the first pages,
		// pagination should not exceed PAGINATION_LENGTH
		const condition1 = pageIndex <= middleDistance && index > PAGINATION_LENGTH - 1
		// in the middle pages,
		// show x pages before currentPage and y pages after currentPage
		const condition2 =
			pageIndex > middleDistance &&
			pageIndex < pageOptions.length - middleDistance &&
			(index < pageIndex - middleDistance || index > pageIndex + middleDistance - 1)
		// in the last pages,
		// always show the x last pages
		const condition3 =
			pageIndex > pageOptions.length - 1 - middleDistance &&
			index < pageOptions.length - PAGINATION_LENGTH
		return condition1 || condition2 || condition3
	}

	return (
		<ProtectedLayout className="c-bicytrust py-5">
			<div className="l-container">
				<div
					className={clsx("c-section-title mb-7 flex flex-row", {
						"px-8": !isMobileOnly,
						"px-6": isMobileOnly,
					})}
				>
					<div className="c-section-title__content justify-between">
						<div className="flex items-center justify-start w-full">
							<PenIcon size={24} />
							<h1 className="c-section-title__title c-h2 text-primary ml-4">
								{t("insurance.list.title", { ns: "bike" })}
							</h1>
						</div>
					</div>
				</div>
				<div
					className={clsx({
						"px-8": !isMobileOnly,
						"px-6": isMobileOnly,
					})}
				>
					<div className="c-card p-5 md:p-12">
						{!isLoading && (
							<>
								<div className="flex-1">
									{/* Global filter */}
									<div
										className={"c-form-group c-form-group--picto mb-7 md:mb-0"}
									>
										<label htmlFor="tableglobalFilter" className="c-label mb-1">
											{t("table.search", { ns: "team" })}
										</label>
										<svg
											className="c-input-picto"
											xmlns="http://www.w3.org/2000/svg"
											height="20"
											width="20"
											viewBox="0 0 512 512"
										>
											<path d="M505.749 475.587l-145.6-145.6c28.203-34.837 45.184-79.104 45.184-127.317C405.333 90.926 314.41.003 202.666.003S0 90.925 0 202.669s90.923 202.667 202.667 202.667c48.213 0 92.48-16.981 127.317-45.184l145.6 145.6c4.16 4.16 9.621 6.251 15.083 6.251s10.923-2.091 15.083-6.251c8.341-8.341 8.341-21.824-.001-30.165zM202.667 362.669c-88.235 0-160-71.765-160-160s71.765-160 160-160 160 71.765 160 160-71.766 160-160 160z" />
										</svg>
										<input
											className={"c-input pl-12 mb-4"}
											id={"tableglobalFilter"}
											name={"tableglobalFilter"}
											type={"text"}
											placeholder={`${t("table.search", { ns: "team" })} ...`}
											value={dataTableFilter}
											style={{ maxWidth: "400px" }}
											onChange={(event) =>
												setDataTableFilter(event.target.value)
											}
										/>
									</div>
								</div>
								<div className="c-grid-table">
									<table
										className="c-grid-table__wrapper mb-8"
										{...getTableProps()}
									>
										<thead>
											{headerGroups.map((headerGroup) => (
												<tr
													className="c-grid-table__head flex uppercase"
													{...headerGroup.getHeaderGroupProps()}
												>
													{headerGroup.headers.map((column) => {
														return (
															<th
																className={
																	"c-table__cell py-5 px-3 w-[160px]"
																}
																{...column.getHeaderProps(
																	column.getSortByToggleProps()
																)}
															>
																{column.render("Header")}
																<span>
																	{column.isSorted
																		? column.isSortedDesc
																			? " 🔽"
																			: " 🔼"
																		: ""}
																</span>
															</th>
														)
													})}
												</tr>
											))}
										</thead>
										<tbody {...getTableBodyProps()}>
											{page.map((row) => {
												prepareRow(row)
												return (
													<tr
														className={
															"c-table__row lg:p-0 text-primary flex"
														}
														{...row.getRowProps()}
													>
														{row.cells.map((cell) => {
															return (
																<td
																	className={
																		"c-table__cell py-5 px-3 w-[160px]"
																	}
																	{...cell.getCellProps()}
																>
																	{cell.render("Cell")}
																</td>
															)
														})}
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
								{data.length === 0 && (
									<p className="text-center mt-5">
										{t("history.no_result", { ns: "partner" })}
									</p>
								)}

								{pageOptions && pageOptions.length > 1 && (
									<div className="flex justify-center mt-5">
										<div className="c-pagination">
											<button
												className="c-btn c-btn--primary px-2 mx-5"
												onClick={() => gotoPage(0)}
												disabled={!canPreviousPage}
											>
												{"<<"}
											</button>
											{pageOptions.map((index) => {
												if (pageShouldBeHiddenInPagination(index))
													return (
														<React.Fragment
															key={index}
														></React.Fragment>
													)
												return (
													<button
														type="button"
														key={index}
														className={
															index === pageIndex ? "font-bold" : ""
														}
														style={{ width: "30px" }}
														onClick={() => gotoPage(index)}
													>
														{index + 1}
													</button>
												)
											})}
											<button
												className="c-btn c-btn--primary px-2 mx-5"
												onClick={() => gotoPage(pageCount - 1)}
												disabled={!canNextPage}
											>
												{">>"}
											</button>
										</div>
									</div>
								)}

								<HelpInfo>
									<HelpCircleIcon size={28} />
									<p className="ml-4 mt-1">
										{t("insurance.help", { ns: "bike" })}
									</p>{" "}
									<NavLink
										data-cy="insurance-help"
										target="_blank"
										to={
											"https://bicytrust.notion.site/0ea2f6426df54ff480ddb176f2fd8aee?v=ec23c9de0cad482aabac7edb95d3949d"
										}
										className="ml-2 mt-1 underline"
									>
										{t("insurance.help_link", { ns: "bike" })}
									</NavLink>
								</HelpInfo>
							</>
						)}
					</div>
				</div>
			</div>
		</ProtectedLayout>
	)
}
